import apiUsuario from "../../../../services/apiUsuario";
import {
  PayloadReconhecimentoFacial,
  RespostaReconhecimentoFacial,
  ApiPoliciaAgilResponse,
} from "../types/mandado-facial.types";

/**
 * Converte um arquivo File para base64
 */
export const convertToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === "string") {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      } else {
        reject(new Error("Erro ao converter arquivo para base64"));
      }
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Mapeia a resposta da API convertendo snake_case para camelCase
 */
export const mapearRespostaAPI = (
  responseData: any
): RespostaReconhecimentoFacial => {
  console.log("🔍 Mapeando resposta da API:", responseData);

  return {
    msg: responseData?.msg || "Resposta da API não informada",
    pessoas:
      responseData?.pessoas?.map((pessoa: any) => ({
        nome: pessoa?.nome || "Não informado",
        mae: pessoa?.mae || "Não informado",
        pai: pessoa?.pai || "Não informado",
        foto: pessoa?.foto || "",
        dataNascimento:
          pessoa?.data_nascimento || pessoa?.dataNascimento || "Não informado",
        grauConfianca: pessoa?.grauConfianca ?? 0,
        statusPessoa: pessoa?.statusPessoa || "",
        mandados: pessoa?.mandados || [],
      })) || [],
  };
};

/**
 * Consulta reconhecimento facial via API Polícia Ágil
 *
 * A API backend gerencia:
 * - Autenticação com API de Mandados (CIIDS)
 * - Cache de token com Redis
 * - Renovação automática de token
 * - Criptografia de credenciais
 *
 * @param cpfUsuario - CPF do usuário autenticado no sistema
 * @param foto - Arquivo de imagem para análise
 * @param latitude - Latitude opcional para auditoria
 * @param longitude - Longitude opcional para auditoria
 */
export const consultarReconhecimentoFacial = async (
  cpfUsuario: string,
  foto: File,
  latitude?: string,
  longitude?: string
): Promise<RespostaReconhecimentoFacial> => {
  try {
    console.log("🚀 INÍCIO DA REQUISIÇÃO DE RECONHECIMENTO FACIAL");

    // Converter imagem para base64
    const base64Image = await convertToBase64(foto);
    console.log("✅ Imagem convertida - Tamanho:", base64Image.length);

    // Criar payload seguindo nova estrutura da API
    const payload: PayloadReconhecimentoFacial = {
      cpf: cpfUsuario,
      imageBase64: base64Image,
      ...(latitude && { lat: latitude }),
      ...(longitude && { long: longitude }),
    };

    console.log("🌐 Enviando requisição para API Polícia Ágil");
    console.log("📍 CPF:", cpfUsuario);
    console.log(
      "📍 Localização:",
      latitude && longitude ? `${latitude}, ${longitude}` : "Não informada"
    );

    const storedToken = sessionStorage.getItem("@webSDS:token");
    if (!storedToken) {
      throw new Error("Usuário não autenticado. Faça login para continuar.");
    }

    // Fazer requisição via API Polícia Ágil (backend gerencia autenticação)
    const response = await apiUsuario.post<
      ApiPoliciaAgilResponse<RespostaReconhecimentoFacial>
    >("/user/mandados/consulta", payload, {
      timeout: 30000,
      headers: {
        Authorization: `Bearer ${storedToken}`,
      },
    });

    console.log("✅ Resposta recebida - Status:", response.status);
    console.log("✅ Resposta recebida - Dados:", response.data);

    if (response.status === 200 && response.data) {
      return mapearRespostaAPI(response.data);
    }

    throw new Error(`Status inesperado: ${response.status}`);
  } catch (error: any) {
    console.error("❌ ERRO na requisição:", error);

    if (error.response) {
      const statusCode = error.response.status;
      const errorData = error.response.data;

      if (statusCode === 401) {
        throw new Error("Sessão expirada. Por favor, faça login novamente.");
      } else if (statusCode === 400) {
        throw new Error(
          errorData?.error ||
            errorData?.message ||
            "Dados inválidos para consulta."
        );
      } else if (statusCode === 500) {
        throw new Error(
          "Erro no servidor. A API de mandados pode estar indisponível. Tente novamente mais tarde."
        );
      } else {
        throw new Error(
          `Erro do servidor (${statusCode}): ${
            errorData?.error || errorData?.message || "Erro desconhecido"
          }`
        );
      }
    } else if (error.request) {
      throw new Error(
        "Erro de conexão com o servidor. Verifique sua internet e tente novamente."
      );
    } else if (error.code === "ECONNABORTED") {
      throw new Error(
        "Tempo de consulta excedido. A análise pode demorar. Tente novamente."
      );
    } else {
      throw new Error(
        error.message || "Erro inesperado ao consultar reconhecimento facial."
      );
    }
  }
};

/**
 * Verifica se o policial logado está habilitado para consulta facial de mandados.
 * Falha fechada: qualquer status != 200 ou erro de rede retorna { enabled: false }.
 */
export const verificarAcessoFacial = async (): Promise<{
  enabled: boolean;
}> => {
  const token = sessionStorage.getItem("@webSDS:token");
  try {
    const response = await apiUsuario.get<{ enabled: boolean }>(
      "/user/mandados/facial/access",
      {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
      }
    );
    if (response.status !== 200) return { enabled: false };
    return response.data;
  } catch {
    return { enabled: false };
  }
};

/**
 * Função helper para determinar cor baseada na confiança
 */
export const getConfidenceColor = (grauConfianca: number): string => {
  if (grauConfianca > 0.7) return "success.main";
  if (grauConfianca > 0.5) return "warning.main";
  return "error.main";
};
